/** Tunables shared by client prediction and server validation. */

/** Pixel size of one map tile. All world coordinates are in pixels. */
export const TILE_SIZE = 32;

/** Avatar collision radius, in pixels. */
export const PLAYER_RADIUS = 10;

/** Walking speed, pixels per second. */
export const WALK_SPEED = 150;

/** How far the server lets a client's self-reported position drift before correcting. */
export const POSITION_TOLERANCE = 48;

/** Rate at which the server broadcasts world snapshots. */
export const SNAPSHOT_HZ = 15;

/**
 * Most ticks carry only your own room. Every Nth also carries everyone else,
 * so distant rooms still look inhabited without costing 15Hz per person.
 */
export const FAR_SNAPSHOT_EVERY = 10;

/** Rate at which the client reports its own position while moving. */
export const INPUT_HZ = 20;

/** Radius of the "local" chat / (later) audio bubble in the open commons, in pixels. */
export const PROXIMITY_RADIUS = 6 * TILE_SIZE;

/** How close you must stand to a surface to interact with it, in pixels. */
export const INTERACT_RADIUS = 2 * TILE_SIZE;

/** Disconnect a socket that has not sent anything (including pings) for this long. */
export const CLIENT_TIMEOUT_MS = 45_000;

/** Client ping interval. Must be comfortably below CLIENT_TIMEOUT_MS. */
export const PING_INTERVAL_MS = 15_000;

/** Scrollback length replayed when you join a channel. */
export const CHAT_HISTORY_LIMIT = 80;

export const MAX_NAME_LENGTH = 24;
export const MAX_CHAT_LENGTH = 1000;
export const MAX_NOTE_LENGTH = 4000;
export const MAX_NOTE_TITLE_LENGTH = 80;

/** Avatar colours players can pick from. */
export const AVATAR_COLORS = [
  '#e05c5c',
  '#e0925c',
  '#e0c65c',
  '#8bc25a',
  '#4fb3a5',
  '#4f96d1',
  '#7a6fd1',
  '#c063b8',
] as const;

/* -------------------------------------------------------------------------- */
/* Character appearance                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Palettes for the generated character sprite.
 *
 * Appearances are stored as indices into these, so the arrays are append-only:
 * reordering them would silently restyle everyone who already saved a look.
 */
export const SKIN_TONES = [
  '#f2c9a0',
  '#e0ab7d',
  '#c68a5e',
  '#a2694a',
  '#7a4a33',
  '#4f3222',
] as const;

export const HAIR_COLORS = [
  '#2b2118',
  '#5a3a22',
  '#9b6b3f',
  '#d9b070',
  '#b8453a',
  '#7a7f87',
  '#e8e6e3',
  '#6b4fa8',
  '#3f7fa8',
  '#3f8f5c',
] as const;

export const CLOTHING_COLORS = [
  '#e05c5c',
  '#e0925c',
  '#e0c65c',
  '#8bc25a',
  '#4fb3a5',
  '#4f96d1',
  '#7a6fd1',
  '#c063b8',
  '#c9ccd4',
  '#4a4f5c',
  '#2f333c',
  '#8a6a4a',
] as const;

/** Hair shapes the sprite renderer knows how to draw. */
export const HAIR_STYLES = [
  'bald',
  'short',
  'messy',
  'bob',
  'long',
  'ponytail',
  'buzz',
  'curly',
] as const;

export type HairStyle = (typeof HAIR_STYLES)[number];

/** Logical pixel grid of the character sprite, before scaling. */
export const SPRITE_WIDTH = 16;
export const SPRITE_HEIGHT = 20;
/** How large the sprite is drawn in the world, in pixels. */
export const SPRITE_SCALE = 2;

/** Largest portrait the server will accept, after client-side downscaling. */
export const AVATAR_PIXELS = 128;
export const AVATAR_MAX_BYTES = 200_000;

/** Sticky-note colours. */
export const NOTE_COLORS = ['#ffd966', '#ffb1a8', '#a8d8ff', '#bfe8b0', '#e2c3f0', '#f2f2f2'] as const;

/** Sizes of every appearance palette, for validation and random generation. */
export const APPEARANCE_SIZES = {
  skin: SKIN_TONES.length,
  hair: HAIR_STYLES.length,
  hairColor: HAIR_COLORS.length,
  shirt: CLOTHING_COLORS.length,
  pants: CLOTHING_COLORS.length,
} as const;
